import { Hours, Milliseconds, millisecondsToHours } from "@pentacode/openapi";
import { hour } from "./consts";

export type Interval = [Date, Date];
export const nullInterval: Interval = [new Date(0), new Date(0)];

export function union(...intervals: Interval[]): Interval[] {
    const itvs = [...intervals].sort(([a], [b]) => Number(a) - Number(b));

    for (let i = 0; i < itvs.length - 1; ) {
        if (itvs[i][1] >= itvs[i + 1][1]) {
            // If the current interval ends at or after the next intervals end, remove the next interval
            itvs.splice(i + 1, 1);
        } else if (itvs[i][1] >= itvs[i + 1][0]) {
            // if the current interval ends after the next interval starts
            // delete the next interval
            // insert new interval with the start of the current interval and the end of the next interval
            itvs.splice(i, 2, [itvs[i][0], itvs[i + 1][1]]);
        } else {
            i++;
        }
    }

    return itvs;
}

// returns the intersection of intervals
export function intersect(...intervals: Interval[]): Interval {
    // latest start date
    const start = new Date(Math.max(...intervals.map((i) => Number(i[0]))));
    // earliest end date, but not earlier than start
    const end = new Date(Math.max(Number(start), Math.min(...intervals.map((i) => Number(i[1])))));
    return [start, end];
}

export function intersectAll(...all: Interval[][]): Interval[] {
    const [one, two, ...rest] = all;

    if (!one) {
        return [];
    }

    if (!two) {
        return one;
    }

    // create a.length * b.length intersections, then reduce them by union
    const intersects: Interval[] = [];
    for (const a of one) {
        for (const b of two) {
            intersects.push(intersect(a, b));
        }
    }
    const result = union(...intersects);

    // recursive call to intersect the resulting intervals with the remaining Arrays of intervals
    return intersectAll(result, ...rest);
}

export function subtractIntervals(a: Interval, b: Interval): Interval[] {
    return (
        /*
         * |---|
         *       |---|
         */
        a[0] >= b[1] || a[1] <= b[0]
            ? [a]
            : /*
               *   |---|
               * |-------|
               */
              a[0] >= b[0] && a[1] <= b[1]
              ? []
              : /*
                 * |-------|
                 *   |---|
                 */
                a[0] < b[0] && a[1] > b[1]
                ? [
                      [a[0], b[0]],
                      [b[1], a[1]],
                  ]
                : /*
                   * |-----|
                   *    |-----|
                   */
                  a[0] < b[0] && a[1] <= b[1]
                  ? [[a[0], b[0]]]
                  : /*
                     *    |-----|
                     * |-----|
                     */
                    a[0] >= b[0] && a[1] > b[1]
                    ? [[b[1], a[1]]]
                    : []
    );
}

export function subtractAllIntervals(a: Interval[], b: Interval[]): Interval[] {
    if (!b.length) {
        return a;
    }

    const result: Interval[] = [];

    for (const aInt of a) {
        const tempRes: Interval[][] = [];
        for (const bInt of b) {
            tempRes.push(subtractIntervals(aInt, bInt));
        }
        result.push(...intersectAll(...tempRes));
    }

    return union(...result);
}

// returns the cumulative overlap between a and each b interval in hours
export function overlap(a: Interval, b: Interval[]): Hours {
    let result = 0;
    for (const itv of b) {
        result += Math.max(0, Math.min(a[1].getTime(), itv[1].getTime()) - Math.max(a[0].getTime(), itv[0].getTime()));
    }
    return millisecondsToHours(result as Milliseconds);
}

export function getIntervalDuration(interval: Interval): Hours {
    if (!interval || !interval[0] || !interval[1]) {
        return 0 as Hours;
    }

    const duration = (interval[1].getTime() - interval[0].getTime()) / hour;
    return duration as Hours;
}
